/**
 * Reward animation — shown when winning a NEW sticker.
 *
 * No text: a big medal with the sticker icon entering with a
 * bounce and a halo of sparkles, over a veil. Designed for ages 2-6: it
 * closes on tapping anywhere (or after an optional auto-dismiss).
 */

import { For, Show } from "solid-js";
import { stickerIcon } from "@melodimals/game-core";
import { useBackDismiss } from "@/lib/use-back-dismiss";

interface RewardPopupProps {
  /** Id of the just-won sticker, or null to show nothing. */
  stickerId: string | null;
  onDismiss: () => void;
}

const SPARKLES = ["✨", "🌟", "⭐", "✨", "🌟", "⭐"];

export function RewardPopup(props: RewardPopupProps) {
  // Browser "Back" dismisses the reward animation (instead of navigating).
  useBackDismiss(
    () => props.stickerId != null,
    () => props.onDismiss(),
  );

  return (
    <Show when={props.stickerId}>
      {(id) => (
        <div
          data-testid="reward-popup"
          role="button"
          tabindex={0}
          aria-label="reward"
          onPointerDown={() => {
            props.onDismiss();
          }}
          onKeyDown={() => {
            props.onDismiss();
          }}
          class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 select-none"
        >
          <div class="relative flex h-56 w-56 items-center justify-center">
            {/* Halo de destellos girando alrededor de la medalla. */}
            <div class="absolute inset-0 animate-spin [animation-duration:6s]" aria-hidden="true">
              <For each={SPARKLES}>
                {(s, i) => (
                  <span
                    class="absolute left-1/2 top-1/2 -ml-4 -mt-4 text-2xl"
                    style={{
                      transform: `rotate(${(i() * 360) / SPARKLES.length}deg) translateY(-7rem)`,
                    }}
                  >
                    {s}
                  </span>
                )}
              </For>
            </div>
            {/* Medalla con el sticker ganado, entra con rebote. */}
            <div
              data-testid="reward-sticker"
              class="flex h-40 w-40 animate-bounce items-center justify-center rounded-full bg-gradient-to-b from-amber-200 to-amber-400 text-7xl shadow-2xl ring-8 ring-white/70"
            >
              <span aria-hidden="true">{stickerIcon(id())}</span>
            </div>
          </div>
        </div>
      )}
    </Show>
  );
}
